if (!customElements.get('mood-occasions-carousel')) {
  class MoodOccasionsCarousel extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.viewport = this.querySelector('[data-gmoc-viewport]');
      this.track = this.querySelector('[data-gmoc-track]');
      this.realCards = Array.from(this.querySelectorAll('[data-gmoc-card]:not([data-gmoc-clone])'));
      this.previousButton = this.querySelector('[data-gmoc-previous]');
      this.nextButton = this.querySelector('[data-gmoc-next]');
      this.status = this.querySelector('[data-gmoc-status]');

      if (!this.viewport || !this.track || !this.realCards.length) return;

      this.cardCount = this.realCards.length;
      const requestedIndex = Number.parseInt(this.dataset.initialIndex, 10);
      this.activeIndex = Number.isNaN(requestedIndex)
        ? 0
        : Math.min(Math.max(requestedIndex, 0), this.cardCount - 1);

      if (this.cardCount > 1) this.buildLoop();

      this.cards = Array.from(this.track.querySelectorAll('[data-gmoc-card]'));
      this.physicalIndex = this.cardCount > 1
        ? this.cardCount + this.activeIndex
        : this.activeIndex;

      this.onPrevious = () => this.step(-1);
      this.onNext = () => this.step(1);
      this.onKeydown = (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.onPrevious();
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.onNext();
        }
      };
      this.onPointerDown = (event) => {
        this.pointerStartX = event.clientX;
        this.suppressClick = false;
      };
      this.onPointerUp = (event) => {
        if (typeof this.pointerStartX !== 'number') return;

        const distance = event.clientX - this.pointerStartX;
        this.pointerStartX = null;

        if (Math.abs(distance) < 45) return;

        this.suppressClick = true;
        this.step(distance > 0 ? -1 : 1);
      };
      this.onTransitionEnd = (event) => {
        if (event.target !== this.track || event.propertyName !== 'transform' || !this.isTransitioning) return;
        window.clearTimeout(this.transitionFallback);
        this.transitionFallback = window.setTimeout(() => this.normalizeLoopPosition(), 80);
      };
      this.onClick = (event) => {
        const scrollTrigger = event.target.closest('[data-gmoc-scroll-to-product]');
        if (scrollTrigger) {
          event.preventDefault();
          const didSync = this.syncProductSelection(scrollTrigger.closest('[data-gmoc-card]'), true);
          if (!didSync) this.scrollToProduct();
          return;
        }

        const trigger = event.target.closest('[data-gmoc-activate]');
        const card = trigger?.closest('[data-gmoc-card]:not([data-gmoc-clone])');
        if (!card) return;

        if (this.suppressClick) {
          this.suppressClick = false;
          return;
        }

        const logicalIndex = Number.parseInt(card.dataset.cardIndex, 10);
        if (logicalIndex !== this.activeIndex) this.select(logicalIndex);
        this.syncProductSelection(card);
      };
      this.onWindowLoad = () => this.align(false);

      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('load', this.onWindowLoad, { once: true });
      this.track.addEventListener('transitionend', this.onTransitionEnd);
      this.viewport.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.viewport.addEventListener('pointerup', this.onPointerUp, { passive: true });
      this.viewport.addEventListener('pointercancel', () => {
        this.pointerStartX = null;
      });

      this.resizeObserver = new ResizeObserver(() => this.align(false));
      this.resizeObserver.observe(this.viewport);

      this.setActiveCard(this.physicalIndex);
      requestAnimationFrame(() => {
        this.align(false);
        this.classList.add('is-ready');
      });

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          if (this.isInitialized) this.align(false);
        }).catch(() => {});
      }

      this.isInitialized = true;
    }

    disconnectedCallback() {
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('load', this.onWindowLoad);
      this.track?.removeEventListener('transitionend', this.onTransitionEnd);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.resizeObserver?.disconnect();
      window.clearTimeout(this.transitionFallback);
      window.clearTimeout(this.postAlignTimer);
      window.clearTimeout(this.alignDurationTimer);
      this.stopCenterTracking();
      this.querySelectorAll('[data-gmoc-clone]').forEach((clone) => clone.remove());
      this.classList.remove('is-ready');
      this.isInitialized = false;
    }

    scrollToProduct() {
      const target = document.querySelector('.gmpd__flavor-grid')
        || document.querySelector('.good-mood-product')
        || document.querySelector('[data-gmpd-section]')
        || document.querySelector('main');
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';

      if (target) {
        target.scrollIntoView({ behavior, block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior });
      }
    }

    syncProductSelection(card, shouldScroll = false) {
      const flavor = card?.dataset.gmocFlavor;
      if (!flavor) return false;

      document.dispatchEvent(new CustomEvent('good-mood:select-options', {
        detail: {
          flavor,
          scroll: shouldScroll,
          scrollTarget: 'flavor-grid'
        }
      }));

      return true;
    }

    buildLoop() {
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();

      this.realCards.forEach((card) => {
        before.appendChild(this.createClone(card));
        after.appendChild(this.createClone(card));
      });

      this.track.prepend(before);
      this.track.append(after);
    }

    createClone(card) {
      const clone = card.cloneNode(true);
      clone.dataset.gmocClone = '';
      clone.classList.remove('is-active');
      clone.removeAttribute('data-initial-active');
      clone.setAttribute('aria-hidden', 'true');

      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
        element.setAttribute('tabindex', '-1');
        if ('disabled' in element) element.disabled = true;
      });

      return clone;
    }

    step(direction) {
      if (this.isTransitioning || this.cardCount < 2) return;
      this.moveToPhysicalIndex(this.physicalIndex + direction);
    }

    select(logicalIndex) {
      if (this.isTransitioning) return;

      const normalizedIndex = this.normalizeLogicalIndex(logicalIndex);
      const candidates = [
        normalizedIndex,
        this.cardCount + normalizedIndex,
        this.cardCount * 2 + normalizedIndex
      ];
      const nearestIndex = candidates.reduce((nearest, candidate) => (
        Math.abs(candidate - this.physicalIndex) < Math.abs(nearest - this.physicalIndex)
          ? candidate
          : nearest
      ));

      this.moveToPhysicalIndex(nearestIndex);
    }

    moveToPhysicalIndex(physicalIndex) {
      const targetCard = this.cards[physicalIndex];
      if (!targetCard) return;

      this.stopCenterTracking();

      const targetCenterBefore = this.getElementCenter(targetCard);
      const currentTranslate = this.getTrackTranslateX();

      this.physicalIndex = physicalIndex;
      this.activeIndex = this.normalizeLogicalIndex(
        Number.parseInt(targetCard.dataset.cardIndex, 10)
      );
      this.isTransitioning = true;

      this.setActiveCard(physicalIndex);

      const targetCenterAfter = this.getElementCenter(targetCard);
      const correctedTranslate = currentTranslate + targetCenterBefore - targetCenterAfter;

      this.track.style.transitionDuration = '0ms';
      this.track.style.transform = `translate3d(${correctedTranslate}px, 0, 0)`;
      this.track.getBoundingClientRect();

      requestAnimationFrame(() => {
        this.track.style.transitionDuration = '0ms';
        this.startCenterTracking();
      });

      window.clearTimeout(this.transitionFallback);
      this.transitionFallback = window.setTimeout(() => this.normalizeLoopPosition(), 760);
    }

    normalizeLoopPosition() {
      this.stopCenterTracking();
      window.clearTimeout(this.transitionFallback);
      window.clearTimeout(this.postAlignTimer);

      let normalizedPhysicalIndex = this.physicalIndex;
      if (this.physicalIndex < this.cardCount) {
        normalizedPhysicalIndex += this.cardCount;
      } else if (this.physicalIndex >= this.cardCount * 2) {
        normalizedPhysicalIndex -= this.cardCount;
      }

      const didNormalizeLoop = normalizedPhysicalIndex !== this.physicalIndex;

      if (didNormalizeLoop) {
        this.physicalIndex = normalizedPhysicalIndex;
        this.setActiveCard(this.physicalIndex);
      }

      this.align(!didNormalizeLoop, '260ms');
      this.isTransitioning = false;
    }

    startCenterTracking(duration = 640) {
      const startedAt = performance.now();

      const trackCenter = (now) => {
        if (!this.isTransitioning) {
          this.centerTrackingFrame = null;
          return;
        }

        const activeCard = this.cards[this.physicalIndex];
        if (!activeCard || !this.viewport || !this.track) {
          this.centerTrackingFrame = null;
          return;
        }

        const currentTranslate = this.getTrackTranslateX();
        const cardRect = activeCard.getBoundingClientRect();
        const viewportRect = this.viewport.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const viewportCenter = viewportRect.left + viewportRect.width / 2;
        const delta = viewportCenter - cardCenter;
        const elapsed = now - startedAt;
        const isSettling = elapsed > duration * 0.72;
        const correctionStrength = isSettling ? 0.18 : 0.085;
        const maxCorrection = isSettling ? 18 : 32;
        const correction = Math.max(
          -maxCorrection,
          Math.min(maxCorrection, delta * correctionStrength)
        );

        this.track.style.transitionDuration = '0ms';

        if (Math.abs(delta) > 0.35) {
          this.track.style.transform = `translate3d(${currentTranslate + correction}px, 0, 0)`;
        }

        if (elapsed < duration) {
          this.centerTrackingFrame = window.requestAnimationFrame(trackCenter);
        } else {
          this.centerTrackingFrame = null;
        }
      };

      this.centerTrackingFrame = window.requestAnimationFrame(trackCenter);
    }

    stopCenterTracking() {
      if (!this.centerTrackingFrame) return;
      window.cancelAnimationFrame(this.centerTrackingFrame);
      this.centerTrackingFrame = null;
    }

    getElementCenter(element) {
      const rect = element.getBoundingClientRect();
      return rect.left + rect.width / 2;
    }

    getTrackTranslateX() {
      const transform = window.getComputedStyle(this.track).transform;
      if (!transform || transform === 'none') return 0;

      if (typeof DOMMatrixReadOnly === 'function') {
        return new DOMMatrixReadOnly(transform).m41;
      }

      const matrix = transform.match(/matrix.*\((.+)\)/);
      if (!matrix) return 0;

      const values = matrix[1].split(',').map((value) => Number.parseFloat(value.trim()));
      return transform.startsWith('matrix3d') ? values[12] || 0 : values[4] || 0;
    }

    normalizeLogicalIndex(index) {
      return ((index % this.cardCount) + this.cardCount) % this.cardCount;
    }

    setActiveCard(physicalIndex) {
      const activeCard = this.cards[physicalIndex];
      if (!activeCard) return;

      this.cards.forEach((card, index) => {
        card.classList.toggle('is-active', index === physicalIndex);
      });

      this.realCards.forEach((card, index) => {
        card.querySelector('[data-gmoc-activate]')?.setAttribute(
          'aria-pressed',
          String(index === this.activeIndex)
        );
      });

      const primaryColor = getComputedStyle(activeCard).getPropertyValue('--gmoc-primary').trim();
      if (primaryColor) this.style.setProperty('--gmoc-current-primary', primaryColor);

      if (this.status) {
        const title = activeCard.dataset.cardTitle || '';
        this.status.textContent = `${this.activeIndex + 1} / ${this.cardCount}: ${title}`;
      }
    }

    align(animate = true, duration = '') {
      const activeCard = this.cards[this.physicalIndex];
      if (!activeCard || !this.viewport || !this.track) return;

      this.track.style.transitionDuration = animate ? duration : '0ms';

      requestAnimationFrame(() => {
        const currentTranslate = this.getTrackTranslateX();
        const cardRect = activeCard.getBoundingClientRect();
        const viewportRect = this.viewport.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const viewportCenter = viewportRect.left + viewportRect.width / 2;
        this.track.style.transform = `translate3d(${currentTranslate + viewportCenter - cardCenter}px, 0, 0)`;

        if (!animate) {
          requestAnimationFrame(() => {
            this.track.style.transitionDuration = '';
          });
        } else if (duration) {
          window.clearTimeout(this.alignDurationTimer);
          this.alignDurationTimer = window.setTimeout(() => {
            this.track.style.transitionDuration = '';
          }, Number.parseFloat(duration) + 60);
        }
      });
    }
  }

  customElements.define('mood-occasions-carousel', MoodOccasionsCarousel);
}
